<template>
    <!-- Contact Form Section -->
    <div id="packaging-inquiry" class="inquiry-section">
        <div class="container mx-auto py-12 px-4">
            <div class="inquiry-content">
                <!-- Left Column: Product Requirements -->
                <div class="form-left">
                    <h3 class="section-title">Describe your needs</h3>
                    <div class="form-group">
                        <label class="form-label">Colors</label>
                        <select 
                            v-model="formData.colors" 
                            class="form-select"
                            :class="{ 'text-gray-400': !formData.colors, 'text-black': formData.colors }">
                            <option value="" disabled>Select Option</option>
                            <option value="custom">Custom Color</option>
                            <option value="white">White</option>
                            <option value="black">Black</option>
                            <option value="brown">Brown</option>
                            <option value="green">Green</option>
                            <option value="blue">Blue</option>
                            <option value="red">Red</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Closure Types</label>
                        <select 
                            v-model="formData.closureTypes" 
                            class="form-select"
                            :class="{ 'text-gray-400': !formData.closureTypes, 'text-black': formData.closureTypes }">
                            <option value="" disabled>Select Option</option>
                            <option value="magnetic">Magnetic Closure</option>
                            <option value="tuck-top">Tuck Top</option>
                            <option value="auto-bottom">Auto Bottom</option>
                            <option value="snap-lock">Snap Lock</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Special Finishes</label>
                        <select 
                            v-model="formData.specialFinishes" 
                            class="form-select"
                            :class="{ 'text-gray-400': !formData.specialFinishes, 'text-black': formData.specialFinishes }">
                            <option value="" disabled>Select Option</option>
                            <option value="foil-stamping">Foil Stamping</option>
                            <option value="embossing">Embossing</option>
                            <option value="debossing">Debossing</option>
                            <option value="uv-coating">UV Coating</option>
                            <option value="spot-uv">Spot UV</option>
                            <option value="none">None</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Custom Inserts</label>
                        <select 
                            v-model="formData.customInserts" 
                            class="form-select"
                            :class="{ 'text-gray-400': !formData.customInserts, 'text-black': formData.customInserts }">
                            <option value="" disabled>Select Option</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                            <option value="not-sure">Not Sure</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Quantity</label>
                        <select 
                            v-model="formData.quantity" 
                            class="form-select"
                            :class="{ 'text-gray-400': !formData.quantity, 'text-black': formData.quantity }">
                            <option value="" disabled>Select Option</option>
                            <option value="100-500">100 - 500 units</option>
                            <option value="500-1000">500 - 1,000 units</option>
                            <option value="1000-5000">1,000 - 5,000 units</option>
                            <option value="5000-10000">5,000 - 10,000 units</option>
                            <option value="10000+">10,000+ units</option>
                        </select>
                    </div>
                </div>

                <!-- Right Column: Contact Information -->
                <div class="form-right">
                    <h3 class="section-title">Fill in your contact information</h3>
                    <div class="form-group">
                        <label class="form-label">Your Name <span class="required">*</span></label>
                        <input 
                            type="text" 
                            v-model="formData.contactName"
                            placeholder="Your Name" 
                            class="form-input"
                            :class="{ 'error': errors.contactName }"
                            required
                        />
                        <span v-if="errors.contactName" class="error-message">{{ errors.contactName }}</span>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Phone Number <span class="required">*</span></label>
                        <input 
                            type="text" 
                            v-model="formData.phoneNumber"
                            placeholder="Phone Number" 
                            class="form-input"
                            :class="{ 'error': errors.phoneNumber }"
                            required
                        />
                        <span v-if="errors.phoneNumber" class="error-message">{{ errors.phoneNumber }}</span>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Email Address <span class="required">*</span></label>
                        <input 
                            type="email" 
                            v-model="formData.email"
                            placeholder="Email Address" 
                            class="form-input"
                            :class="{ 'error': errors.email }"
                            required
                        />
                        <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Business Name</label>
                        <input 
                            type="text" 
                            v-model="formData.businessName"
                            placeholder="Business Name" 
                            class="form-input"
                        />
                    </div>
                    <div class="form-group">
                        <label class="form-label">Additional Notes</label>
                        <textarea 
                            v-model="formData.additionalNotes"
                            placeholder="Additional Notes" 
                            class="form-textarea"
                            rows="4"
                        ></textarea>
                    </div>
                    <button 
                        class="btn-submit" 
                        @click="submitForm"
                        :disabled="isSubmitting"
                    >
                        <span v-if="!isSubmitting">Submit Your Inquiry</span>
                        <span v-else>Submitting...</span>
                    </button>
                </div>
            </div>
        </div>
        <toast-message ref="toastMessageRef" />
    </div>
</template>

<script setup lang="ts">
    import { ref, onMounted } from 'vue'
    import { useHttp } from '~/utils/httpUtil'
    import cmsUtils from '~/utils/cmsUtils'
    import toastMessage from '../common/toastMessage.vue'
    import type { Response } from '~/types'

    const toastMessageRef = ref<InstanceType<typeof toastMessage> | null>(null)
    const isSubmitting = ref(false)
    const cmsInfo = ref<any>(null)
    const timestampStr = ref('')
    let randomStr = ''

    // Form data
    const formData = ref({
        colors: '',
        closureTypes: '',
        specialFinishes: '',
        customInserts: '',
        quantity: '',
        contactName: '',
        phoneNumber: '',
        email: '',
        businessName: '',
        additionalNotes: ''
    })

    // Form errors
    const errors = ref({
        contactName: '',
        phoneNumber: '',
        email: ''
    })

    // Initialize
    onMounted(async () => {
        try {
            cmsInfo.value = await cmsUtils.getCmsInfo()
            const res = await useHttp("/nuxt/common/getTimestamp", {
                method: 'POST',
                body: {},
                headers: {}
            }) as Response<string>
            timestampStr.value = res.data
        } catch (error) {
            console.error('Error initializing form:', error)
        }
    })

    // Generate Token
    async function generateToken(websiteIdStr: string, timestampStr: string) {
        let randomArray = new Uint8Array(16)
        window.crypto.getRandomValues(randomArray)
        randomStr = Array.from(randomArray).map(b => b.toString(16).padStart(2, '0')).join('')
        const data = `${websiteIdStr}:${timestampStr}:${randomStr}`
        
        const hash = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(data))
        const hashArray = Array.from(new Uint8Array(hash))
        const token = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
        return token
    }

    // Validate form
    function validateForm(): boolean {
        let isValid = true
        errors.value = {
            contactName: '',
            phoneNumber: '',
            email: ''
        }

        if (!formData.value.contactName.trim()) {
            errors.value.contactName = 'Please enter your name'
            isValid = false
        }

        if (!formData.value.phoneNumber.trim()) {
            errors.value.phoneNumber = 'Please enter your phone number'
            isValid = false
        } else {
            const phoneRegex = /^[\+]?[1-9][\d\-]{0,15}$/
            if (!phoneRegex.test(formData.value.phoneNumber.replace(/[\s\-\(\)]/g, ''))) {
                errors.value.phoneNumber = 'Please enter a valid phone number'
                isValid = false
            }
        }

        if (!formData.value.email.trim()) {
            errors.value.email = 'Please enter your email address'
            isValid = false
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(formData.value.email)) {
                errors.value.email = 'Please enter a valid email address'
                isValid = false
            }
        }

        return isValid
    }

    // Reset form
    function resetForm() {
        formData.value = {
            colors: '',
            closureTypes: '',
            specialFinishes: '',
            customInserts: '',
            quantity: '',
            contactName: '',
            phoneNumber: '',
            email: '',
            businessName: '',
            additionalNotes: ''
        }
        errors.value = {
            contactName: '',
            phoneNumber: '',
            email: ''
        }
    }

    // Submit form
    async function submitForm() {
        if (!validateForm()) {
            toastMessageRef.value?.showFail('Please correct the errors in the form.')
            return
        }

        if (!cmsInfo.value || !timestampStr.value) {
            toastMessageRef.value?.showFail('System is initializing, please try again later.')
            return
        }

        isSubmitting.value = true

        try {
            const token = await generateToken(cmsInfo.value.website.id.toString(), timestampStr.value)
            
            const formDataObj = new FormData()
            formDataObj.append('contactName', formData.value.contactName)
            formDataObj.append('email', formData.value.email)
            formDataObj.append('phoneNumber', formData.value.phoneNumber)
            formDataObj.append('companyName', formData.value.businessName || '')
            formDataObj.append('message', `Product Requirements:
Colors: ${formData.value.colors || 'Not specified'}
Closure Types: ${formData.value.closureTypes || 'Not specified'}
Special Finishes: ${formData.value.specialFinishes || 'Not specified'}
Custom Inserts: ${formData.value.customInserts || 'Not specified'}
Quantity: ${formData.value.quantity || 'Not specified'}

Additional Notes: ${formData.value.additionalNotes || 'None'}`)
            formDataObj.append('token', token)
            formDataObj.append('websiteId', cmsInfo.value.website.id.toString())
            formDataObj.append('timestamp', timestampStr.value)
            formDataObj.append('randomStr', randomStr)
            // 来源页面 URL（用于追溯留言提交位置）
            formDataObj.append('sourceUrl', typeof window !== 'undefined' ? window.location.href : '')

            const res = await useHttp("/nuxt/common/addUserConsultant", {
                method: 'POST',
                body: formDataObj,
                headers: {}
            }) as Response<any>

            if (res.code === 200) {
                toastMessageRef.value?.showSuccess('Thank you for your inquiry! We will reply to you as soon as possible!')
                resetForm()
            } else if (res.code === 401) {
                toastMessageRef.value?.showFail('Token is timeout, please refresh the page and try again.')
            } else {
                toastMessageRef.value?.showFail('Server error, please contact administrator!')
            }
        } catch (error) {
            console.error('Error submitting form:', error)
            toastMessageRef.value?.showFail('There was an error submitting your form. Please try again.')
        } finally {
            isSubmitting.value = false
        }
    }
</script>

<style scoped lang="scss">
    .inquiry-section {
        width: 100%;
        background-color: #f9fafb;
        padding: 60px 0;
    }

    .inquiry-content {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 60px;
        max-width: 1200px;
        margin: 0 auto;

        @media (max-width: 1024px) {
            grid-template-columns: 1fr;
            gap: 40px;
        }
    }

    .form-left,
    .form-right {
        background-color: #fff;
        padding: 40px;
        border-radius: 12px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

        @media (max-width: 768px) {
            padding: 24px;
        }
    }

    .section-title {
        font-size: 28px;
        font-weight: 600;
        color: #111827;
        margin-bottom: 32px;
        padding-bottom: 16px;
        border-bottom: 2px solid #e5e7eb;

        @media (max-width: 768px) {
            font-size: 24px;
            margin-bottom: 24px;
        }
    }

    .form-group {
        margin-bottom: 24px;

        .form-label {
            display: block;
            font-size: 14px;
            font-weight: 600;
            color: #374151;
            margin-bottom: 8px;

            .required {
                color: #ef4444;
            }
        }

        .form-select,
        .form-input,
        .form-textarea {
            width: 100%;
            padding: 12px 16px;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            font-size: 16px;
            transition: all 0.3s;
            background-color: #fff;

            &:focus {
                outline: none;
                border-color: #4CAF50;
                box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
            }

            &.error {
                border-color: #ef4444;
            }

            &::placeholder {
                color: #9ca3af;
            }
        }

        .form-textarea {
            resize: vertical;
            min-height: 100px;
            font-family: inherit;
        }

        .error-message {
            display: block;
            color: #ef4444;
            font-size: 12px;
            margin-top: 4px;
        }
    }

    .btn-submit {
        width: 100%;
        background-color: #06b6d4;
        color: white;
        border: none;
        padding: 14px 32px;
        border-radius: 8px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: background-color 0.3s;
        margin-top: 8px;

        &:hover:not(:disabled) {
            background-color: #0891b2;
        }

        &:disabled {
            background-color: #9ca3af;
            cursor: not-allowed;
        }
    }
</style>
